import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, BadgeCheck, Check, Navigation, Download, Play, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SITE } from "@/data/site";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ = [
  { q: "Are these plots bank loan eligible?", a: "Yes, all our approved plots are eligible for bank loans and we provide complete loan assistance." },
  { q: "Is the title clear and litigation-free?", a: "Absolutely. Every project comes with a 100% clear title guarantee verified by our legal team." },
  { q: "Can I visit the site before booking?", a: "Yes! We offer free site visits with pickup and drop facility. Book online or via WhatsApp." },
];

export default function ProjectDetails() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  useEffect(() => {
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => nav("/projects"));
  }, [slug, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Please enter your name and phone");
    await api.post("/leads", { ...form, source: "Website", project_id: p.id, project_name: p.name });
    toast.success("Enquiry sent! Our team will call you shortly.");
    setForm({ name: "", phone: "", message: "" });
  };

  if (!p) return <div className="min-h-screen grid place-items-center text-[#0B5D3B]">Loading…</div>;

  return (
    <div className="font-body">
      <div className="relative h-[52vh]">
        <img src={p.images?.[active]} alt={p.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-5 md:px-8 pb-8 text-white">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] text-black px-3 py-1 text-xs font-semibold mb-3"><BadgeCheck size={14}/> {p.approval} Approved</span>
          <h1 className="font-heading text-3xl lg:text-5xl font-bold">{p.name}</h1>
          <p className="flex items-center gap-1.5 mt-2 text-white/85"><MapPin size={16}/> {p.location}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          {/* gallery */}
          <div className="grid grid-cols-4 gap-3" data-testid="project-gallery">
            {p.images?.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} className={`rounded-lg overflow-hidden h-20 md:h-24 ${active===i?"ring-2 ring-[#0B5D3B]":""}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-3">About This Project</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{p.description}</p>
          </div>

          {/* amenities */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-5">Amenities</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {p.amenities?.map((a) => (
                <div key={a} className="flex items-center gap-2.5 rounded-lg bg-[#f8faf9] px-4 py-3 text-sm text-gray-700"><Check size={16} className="text-[#0B5D3B]"/> {a}</div>
              ))}
            </div>
          </div>

          {/* drone video */}
          {p.drone_video && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2"><Play size={20} className="text-[#0B5D3B]"/> Drone View</h2>
              <div className="aspect-video rounded-xl overflow-hidden border border-gray-100 bg-black">
                {p.drone_video.includes("youtube") || p.drone_video.includes("youtu.be") || p.drone_video.includes("/embed/")
                  ? <iframe src={p.drone_video} title="Drone" className="w-full h-full" allowFullScreen />
                  : <video src={p.drone_video} controls className="w-full h-full" />}
              </div>
            </div>
          )}

          {/* project videos */}
          {p.videos?.length > 0 && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2"><Play size={20} className="text-[#0B5D3B]"/> Project Videos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {p.videos.map((v, i) => (
                  <div key={i} className="aspect-video rounded-xl overflow-hidden border border-gray-100 bg-black">
                    <video src={v} controls className="w-full h-full" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* nearby */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-5">Nearby Places</h2>
            <div className="space-y-2.5">
              {p.nearby?.map((n) => (
                <div key={n} className="flex items-center gap-2.5 text-gray-700"><Navigation size={15} className="text-[#D4AF37]"/> {n}</div>
              ))}
            </div>
          </div>

          {/* map */}
          {p.map_embed && (
            <div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-5">Location Map</h2>
              <div className="aspect-video rounded-xl overflow-hidden border border-gray-100">
                <iframe src={p.map_embed} title="Map" className="w-full h-full" loading="lazy" />
              </div>
            </div>
          )}

          {/* FAQ */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-5">FAQ</h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`}>
                  <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {/* sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-5">
          <div className="rounded-2xl border border-gray-100 shadow-[0_12px_40px_rgba(11,93,59,0.1)] p-6">
            <p className="text-xs uppercase tracking-wide text-gray-400">Starting Price</p>
            <p className="font-heading text-3xl font-bold text-[#0B5D3B]">{p.price}</p>
            <p className="text-sm text-gray-500 mt-1">Plot sizes: {p.size}</p>
            <div className="mt-5 space-y-2">
              <button data-testid="sidebar-book-visit" onClick={()=>nav("/book-visit")} className="w-full rounded-full bg-[#0B5D3B] text-white py-3 font-semibold hover:bg-[#094e32] transition-colors">Book Free Site Visit</button>
              <a href={SITE.whatsapp(`Hi, I'm interested in ${p.name}`)} target="_blank" rel="noopener noreferrer" className="block text-center w-full rounded-full border-2 border-[#25D366] text-[#25D366] py-3 font-semibold hover:bg-[#25D366]/5 transition-colors">WhatsApp Enquiry</a>
            </div>
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-gray-100 p-6 space-y-3" data-testid="enquiry-form">
            <h3 className="font-heading font-semibold text-gray-900">Quick Enquiry</h3>
            <input data-testid="enquiry-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Your Name" className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5D3B]" />
            <input data-testid="enquiry-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} placeholder="Phone Number" className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5D3B]" />
            <textarea data-testid="enquiry-message" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} placeholder="Message (optional)" rows={3} className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5D3B]" />
            <button data-testid="enquiry-submit" className="w-full rounded-full bg-[#D4AF37] text-black py-3 font-semibold hover:bg-[#c9a431] transition-colors">Send Enquiry</button>
          </form>

          {(p.brochure_url || p.layout_url || p.master_layout) ? (
            <div className="space-y-2" data-testid="downloads-block">
              {p.brochure_url && (
                <a href={p.brochure_url} target="_blank" rel="noopener noreferrer" download className="flex items-center justify-center gap-2 rounded-2xl border border-gray-100 py-4 text-sm font-semibold text-[#0B5D3B] hover:bg-[#f8faf9] transition-colors" data-testid="download-brochure">
                  <Download size={17}/> Download Brochure & Price List
                </a>
              )}
              {(p.layout_url || p.master_layout) && (
                <a href={p.layout_url || p.master_layout} target="_blank" rel="noopener noreferrer" download className="flex items-center justify-center gap-2 rounded-2xl border border-gray-100 py-4 text-sm font-semibold text-[#0B5D3B] hover:bg-[#f8faf9] transition-colors" data-testid="download-layout">
                  <Download size={17}/> Download Master Layout
                </a>
              )}
            </div>
          ) : (
            <a href={SITE.whatsapp(`Please send the brochure for ${p.name}`)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-2xl border border-gray-100 py-4 text-sm font-semibold text-[#0B5D3B] hover:bg-[#f8faf9] transition-colors" data-testid="download-brochure">
              <Download size={17}/> Download Brochure & Price List
            </a>
          )}
        </aside>
      </div>
    </div>
  );
}
